// src/screens/s3/LifeDesign.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Life Design
// Part A: Summary of committed scenarios
// Part B: Generate Statement + editable textarea
// Part C: Legacy Note
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { generateLifeDesignStatement } from '../../utils/generateLifeDesignStatement';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const SCENARIO_LABELS: Record<string, { name: string; emoji: string }> = {
  earlier: { name: 'Earlier Retirement', emoji: '⏭️' },
  phased: { name: 'Phased Retirement', emoji: '🌱' },
  oneoff: { name: 'One-Off Spend', emoji: '🎁' },
  stress: { name: 'Stress Test', emoji: '⚡' },
  longevity: { name: 'Longevity', emoji: '📅' },
  lifestyle: { name: 'Lifestyle Business', emoji: '💼' },
};

export default function LifeDesign({ data, onUpdate }: Props) {
  const allScenarios = ['earlier', 'phased', 'oneoff', 'stress', 'longevity', 'lifestyle'];
  const committed = data.s3ScenariosCommitted || [];
  const skipped = allScenarios.filter((s) => !committed.includes(s));

  const handleGenerateStatement = () => {
    const statement = generateLifeDesignStatement(data);
    onUpdate({ s3LifeDesignStatement: statement });
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-6">
      <h2 className="text-xl font-bold text-ma-navy mb-2">Life Design</h2>
      <p className="text-gray-600 mb-8">
        A summary of your retirement scenarios and a statement for your plan.
      </p>

      {/* Part A: Scenario Summary */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
          Explored Scenarios
        </div>

        <div className="space-y-2">
          {allScenarios.map((scenario) => {
            const isCommitted = committed.includes(scenario);
            const label = SCENARIO_LABELS[scenario];
            return (
              <div
                key={scenario}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  isCommitted
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200 opacity-50'
                }`}
              >
                <span className="text-lg">{label.emoji}</span>
                <span className={`text-sm ${isCommitted ? 'text-green-800 font-medium' : 'text-gray-500 line-through'}`}>
                  {label.name}
                </span>
                {isCommitted && <span className="text-green-600 font-bold text-sm ml-auto">✓ Committed</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Part B: Life Design Statement */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
          Life Design Statement
        </div>

        <button
          type="button"
          onClick={handleGenerateStatement}
          className="mb-4 px-4 py-2.5 bg-ma-navy text-white rounded-lg text-sm font-semibold hover:bg-opacity-90 transition"
        >
          Generate Statement
        </button>

        <div>
          <textarea
            value={data.s3LifeDesignStatement}
            onChange={(e) => onUpdate({ s3LifeDesignStatement: e.target.value })}
            placeholder="Your life design statement will appear here..."
            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-ma-navy/20 resize-none"
            rows={4}
          />
        </div>

        {data.s3LifeDesignStatement && (
          <div className="mt-4 bg-ma-navy text-white rounded-lg p-6">
            <p className="text-sm leading-relaxed">{data.s3LifeDesignStatement}</p>
          </div>
        )}
      </div>

      {/* Part C: Legacy Note */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
          Legacy Note
        </div>

        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
            What do you want to leave behind?
          </label>
          <textarea
            value={data.s3LegacyNote}
            onChange={(e) => onUpdate({ s3LegacyNote: e.target.value })}
            placeholder="e.g. funds for education, a gift to charity, a family business..."
            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-ma-navy/20 resize-none"
            rows={3}
          />
        </div>
      </div>
    </div>
  );
}
